import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cx } from '@/cva.config';

interface InfoTooltipProps {
  body: string;
  label: string;
  placement?: 'top' | 'right' | 'bottom';
}

export const InfoTooltip = ({
  label,
  body,
  placement = 'top',
}: InfoTooltipProps) => {
  const [open, setOpen] = useState(false);
  const enterTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const leaveTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(enterTimeout.current);
      clearTimeout(leaveTimeout.current);
    },
    [],
  );

  const handleEnter = () => {
    clearTimeout(leaveTimeout.current);
    enterTimeout.current = setTimeout(() => setOpen(true), 120);
  };

  const handleLeave = () => {
    clearTimeout(enterTimeout.current);
    leaveTimeout.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      role="tooltip"
    >
      <button
        aria-label={`About ${label}`}
        className="inline-flex size-4 items-center justify-center  bg-surface font-mono text-2xs text-subtle transition-colors hover:text-foreground"
        onClick={() => setOpen((previous) => !previous)}
        type="button"
      >
        <Info size={10} />
      </button>
      {open && (
        <span
          className={cx(
            'pointer-events-none absolute z-50 w-96 animate-fadeIn rounded-md border border-border-active bg-surface px-3 py-2 text-xs/snug text-foreground shadow-xl',
            placement === 'top' && 'bottom-full left-1/2 mb-2 -translate-x-1/2',
            placement === 'right' && 'top-1/2 left-full ml-2 -translate-y-1/2',
            placement === 'bottom' && 'top-full left-1/2 mt-2 -translate-x-1/2',
          )}
          role="tooltip"
        >
          <span className="mb-1 block font-mono text-xs tracking-wider text-subtle uppercase">
            {label}
          </span>
          <span className="block text-balance text-foreground-muted">
            {body}
          </span>
        </span>
      )}
    </span>
  );
};
